import {useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {Formik, Form} from 'formik';
import {useAppDispatch} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import Container from '../../components/common/layout/Container';
import {Button, ButtonWithLoader} from '../common/Button';
import ActionButtons from '../common/layout/ActionButtons';
import {addTenant} from '../../api/tenants';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import {CreateTenantProps, OptionsProp} from '../../constants/types';
import routes from './routes';
import {addTenantValidationSchema} from '../../constants/validationSchemes';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {useTranslation} from 'react-i18next';
import {FormGroup} from '@mui/material';
import {AutocompleteField, TextField} from '../common/formik/fields';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const options: OptionsProp[] = [
  {value: 'ENDODONTICS', label: 'Endodontics'}
];

const initialValues = {
  slug: '',
  name: '',
  industryCode: options[0].value
};

const AddTenant = () => {
  const {t} = useTranslation('tenants');
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [apiValidationError, setApiValidationError] =
   useState<Nullable<Dictionary<string[]>>>(null);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const clearMessages = () => {
    setApiValidationError(null);
  };

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  const onSubmit = async (values: CreateTenantProps) => {
    try {
      await addTenant(values);
      history.push(routes.tenants());
      addSuccessToast(t('toasts.createTenantMessage'));
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'tenant');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  return (
    <Container header="System Configuration - Tenants">
      <h2 className="subheader">Create New Tenant</h2>

      <Formik
        initialValues={initialValues}
        validationSchema={addTenantValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({setFieldTouched, isSubmitting, isValid, dirty}) => {
          useOnUnsavedChanges(dirty);

          return (
            <Form className="form">
              <div className="creation-form-wrapper">
                <FormGroup>
                  <TextField
                    label="Tenant ID"
                    id="tenantId"
                    name="slug"
                    required
                    onBlur={() => {
                      setFieldTouched('slug', true);
                      clearMessages();
                    }}
                    disabled={isSubmitting}
                    apiValidationError={apiValidationError}
                  />
                </FormGroup>

                <FormGroup>
                  <TextField
                    label="Tenant Name"
                    id="tenantName"
                    name="name"
                    required
                    onBlur={() => {
                      setFieldTouched('name', true);
                      clearMessages();
                    }}
                    disabled={isSubmitting}
                    apiValidationError={apiValidationError}
                  />
                </FormGroup>

                <FormGroup>
                  <AutocompleteField
                    name="industryCode"
                    label="Industry"
                    options={options}
                    disabled
                  />
                </FormGroup>
              </div>

              <ActionButtons>
                <ButtonWithLoader
                  testId="create-tenant"
                  disabled={!isValid}
                  isSubmitting={isSubmitting}
                  type="submit"
                  label="Create new tenant"
                />

                <Button
                  onClick={() => {
                    history.push(routes.tenants());
                  }}
                  label="Cancel"
                  variant="outlined"
                  color="secondary"
                />
              </ActionButtons>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AddTenant;
